// Ambalaj (kutu/paket/koli/palet) satırları: sepet satırı stok için ana birimde durur (48 adet),
// ambalaj adı ve içeriği satırda taşınır. Fişte ve yazar kasada "2 × KOLİ" olarak gösterilir ki
// birim fiyat kuruşa yuvarlanırken toplam kaymasın.

export interface PackageLineFields {
  name: string;
  quantity: number;
  price: number;
  packageName?: string;
  packageFactor?: number;
}

const EPS = 1e-6;

export const isPackageLine = (item: { packageName?: string; packageFactor?: number }): boolean =>
  !!item.packageName && (item.packageFactor ?? 0) > 0;

/** Satır tam ambalaj katıysa ambalaj adedi (2), değilse null. */
export function packageCount(item: { quantity: number; packageName?: string; packageFactor?: number }): number | null {
  if (!isPackageLine(item)) return null;
  const count = item.quantity / (item.packageFactor as number);
  const rounded = Math.round(count);
  return rounded > 0 && Math.abs(count - rounded) < EPS ? rounded : null;
}

/** Sepette gösterilen etiket: "2 KOLİ". Tam kat değilse null. */
export function packageLabel(item: { quantity: number; packageName?: string; packageFactor?: number }): string | null {
  const count = packageCount(item);
  return count == null ? null : `${count} ${item.packageName!.toLocaleUpperCase('tr-TR')}`;
}

/** Fiş/yazar kasa satır adı: "KOLİx24 Coca-Cola 330 ml" (cihazlar adı 22-24 karakterde keser). */
export function packageLineName(item: { name: string; packageName?: string; packageFactor?: number }): string {
  if (!isPackageLine(item)) return item.name;
  const factor = Number((item.packageFactor as number).toFixed(3));
  return `${item.packageName!.toLocaleUpperCase('tr-TR')}x${factor} ${item.name}`;
}

/**
 * Ana birim fiyatından ambalaj fiyatı. Kuruşa yuvarlama cihaza/fişe bırakılır (KDV hariç fiyat
 * bekleyen kasalarda erken yuvarlama kuruş kaydırır); yalnız kayan nokta artığı temizlenir
 * (20,8333…×24 = 500).
 */
export function packageUnitPrice(basePrice: number, factor: number): number {
  return Math.round(basePrice * factor * 1_000_000) / 1_000_000;
}

/**
 * Fiş ya da yazar kasa satırını ambalaja çevirir: tam kat ise miktar = ambalaj adedi,
 * fiyat = ambalaj fiyatı, ad = "KOLİx24 Ürün". Fiyat alanı `price` ya da `unitPrice` olabilir;
 * hangisi varsa o ölçeklenir (alan içindeki birim fiyat ana birim içindir).
 * Ambalaj satırı değilse ya da tam kat değilse satır aynen döner.
 */
export function toPackageFiscalLine<T extends { name: string; quantity: number; price?: number; unitPrice?: number }>(
  cartItem: { quantity: number; name: string; packageName?: string; packageFactor?: number },
  line: T,
): T {
  const count = packageCount(cartItem);
  if (count == null) return line;
  const factor = cartItem.packageFactor as number;
  const next: T = { ...line, name: packageLineName(cartItem), quantity: count };
  if (typeof line.price === 'number') next.price = packageUnitPrice(line.price, factor);
  if (typeof line.unitPrice === 'number') next.unitPrice = packageUnitPrice(line.unitPrice, factor);
  return next;
}
